import React from "react";
import { Edit, Upload, Trash2 } from "lucide-react";

const ProductCard = ({
  product,
  productData,
  selectedCategory,
  selectedSubcategory,
  Segment,
  filteredProducts,
  allProducts,
  hasStyleSelected,
  getCurrentDetails,
  getCurrentImage,
  calculateTotalWastage,
  handleCardClick,
  handleStyleSelect,
  handleImageUpload,
  handleDeleteStyle,
  styleTypes,
  isCategoryConfigured,
}) => {
  const selectedStyle = productData[product.id].selectedStyleType;
  const currentDetails = getCurrentDetails(product.id);
  const currentImage = getCurrentImage(product.id);
  const totalWastage = calculateTotalWastage(currentDetails);

  const isStyleFilled = (styleType) => {
    const styleData = productData[product.id]?.[styleType];
    if (!styleData) return false;
    const details = styleData.details;
    return !!(
      details?.purity ||
      details?.wastage ||
      details?.setMC ||
      details?.netGramMC ||
      details?.specificationMC ||
      details?.specificationGramRate
    );
  };

  const deleteCurrentStyle = () => {
    if (selectedStyle) {
      handleDeleteStyle(product.id, selectedStyle);
    }
  };

  return (
    <div
      key={product.id}
      className={`registration-req-product-card ${
        hasStyleSelected(product.id) ? "selected" : ""
      }`}
      onClick={() => handleCardClick(product.id)}
    >
      <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
        <div className="product-card-header">
          <div className="product-image-upload-area">
            {currentImage ? (
              <img
                src={currentImage}
                alt={product.name}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    "https://placehold.co/60x60/E0E0E0/333333?text=Error";
                }}
              />
            ) : (
              <Upload color="#999" size={24} />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload(e, product.id)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div style={{ flex: 1 }}>
            <h4 className="product-name">{product.name}</h4>
            <p className="product-specs">
              {product.segment} / {product.categoryId} / {product.subcategoryId}
            </p>
            <div className="style-buttons-container">
              {product.styleTypes.map((styleType) => {
                const isFilled = isStyleFilled(styleType);
                const isCurrentSelected = selectedStyle === styleType;

                if (isCurrentSelected) {
                  return (
                    <div key={styleType} className="style-button-wrapper">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStyleSelect(product.id, styleType);
                        }}
                        className={`style-button ${
                          selectedStyle === styleType ? "selected" : ""
                        }`}
                      >
                        {styleType}
                        <Edit size={12} color="white" />
                      </button>
                    </div>
                  );
                }

                return (
                  <div key={styleType} className="style-button-wrapper">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStyleSelect(product.id, styleType);
                      }}
                      className={`style-button ${
                        selectedStyle === styleType ? "selected" : ""
                      }`}
                    >
                      {styleType}
                    </button>
                    {isFilled && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStyleSelect(product.id, styleType);
                        }}
                        className="style-delete-button"
                        title="Edit style"
                      >
                        <Edit size={12} color="white" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div
          className={`selection-indicator ${
            hasStyleSelected(product.id) ? "selected" : ""
          }`}
        ></div>
      </div>
      <div>
        {" "}
        {hasStyleSelected(product.id) &&
          currentDetails &&
          currentDetails.purity && (
            <div className="metrics-section">
              <div className="metrics-header">
                <span className="metrics-title">
                  View Metrics - {selectedStyle}
                </span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => {
                      selectedStyle &&
                        handleStyleSelect(product.id, selectedStyle);
                    }}
                    className="style-edit-button metrics-edit-btn"
                    title="Edit style"
                  >
                    <Edit size={12} color="white" />
                  </button>
                  <button
                    onClick={deleteCurrentStyle}
                    className="style-delete-button metrics-delete-btn"
                    title="Delete style"
                  >
                    <Trash2 size={12} color="white" />
                  </button>
                </div>
              </div>
              <div className="metric-item">
                <div className="metric-grid">
                  <div>
                    Purity:{" "}
                    <span className="metric-value">
                      {currentDetails.purity
                        ? `${currentDetails.purity}%`
                        : "-"}
                    </span>
                  </div>

                  <div>
                    Specification:{" "}
                    <span className="metric-value">
                      {currentDetails.specification || "-"}
                    </span>
                  </div>

                  <div>
                    Wastage:{" "}
                    <span className="metric-value">
                      {currentDetails.wastage
                        ? `${currentDetails.wastage}%`
                        : "-"}
                    </span>
                  </div>

                  {currentDetails.specification === "PLANE" ? (
                    <>
                      <div>
                        Set MC:{" "}
                        <span className="metric-value">
                          {currentDetails.setMC || "-"}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        Set MC:{" "}
                        <span className="metric-value">
                          {currentDetails.setMC || "-"}
                        </span>
                      </div>
                      <div>
                        Spec Wastage:{" "}
                        <span className="metric-value">
                          {currentDetails.specificationMC
                            ? `${currentDetails.specificationMC}%`
                            : "-"}
                        </span>
                      </div>

                      <div>
                        Spec Gram Rate:{" "}
                        <span className="metric-value">
                          {currentDetails.specificationGramRate || "-"}
                        </span>
                      </div>
                    </>
                  )}
                  <div>
                    Total Wastage:{" "}
                    <span className="metric-value">
                      {typeof totalWastage === "number" && !isNaN(totalWastage)
                        ? `${totalWastage.toFixed(3)}%`
                        : "-"}
                    </span>
                  </div>
                  <div>
                    Net Gram MC:{" "}
                    <span className="metric-value">
                      {currentDetails.netGramMC || "-"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default ProductCard;
